import { CartUpdateEvent } from '@theme/events';

/**
 * Automatically adds/removes the free gift (Noctaseal Sleep Mask) line item
 * to/from the cart once the cart subtotal (excluding the gift itself)
 * crosses the free-gift threshold. The BXGY automatic discount only ever
 * discounts a gift that is ALREADY in the cart — it never adds it — so this
 * script is what makes the gift actually appear (and disappear if the
 * customer drops back below the threshold) without any app.
 *
 * The threshold here must stay in sync with the one used to render the
 * progress bar in snippets/cart-summary.liquid.
 */
(function () {
  var GIFT_VARIANT_ID = 56310338322817; // Noctaseal Sleep Mask – Default Title
  var THRESHOLD_CENTS = 3995; // €39.95
  var GIFT_PROPERTY_KEY = '_free_gift';
  var GIFT_PROPERTY_VALUE = 'true';
  var SOURCE_ID = 'free-gift-auto-add';

  var processing = false;
  var pendingRerun = false;

  /** @returns {Promise<Object>} */
  function fetchCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (response) {
      return response.json();
    });
  }

  /**
   * @param {Object} cart
   * @returns {Object|null}
   */
  function findGiftLine(cart) {
    for (var i = 0; i < cart.items.length; i++) {
      var item = cart.items[i];
      if (item.variant_id === GIFT_VARIANT_ID && item.properties && item.properties[GIFT_PROPERTY_KEY] === GIFT_PROPERTY_VALUE) {
        return item;
      }
    }
    return null;
  }

  /**
   * Subtotal of everything in the cart EXCLUDING our auto-added gift line,
   * so a customer manually buying the Sleep Mask still counts toward the
   * threshold, while the free copy never does.
   * @param {Object} cart
   * @returns {number}
   */
  function eligibleSubtotalCents(cart) {
    var sum = 0;
    for (var i = 0; i < cart.items.length; i++) {
      var item = cart.items[i];
      var isGift = item.variant_id === GIFT_VARIANT_ID && item.properties && item.properties[GIFT_PROPERTY_KEY] === GIFT_PROPERTY_VALUE;
      if (!isGift) sum += item.line_price;
    }
    return sum;
  }

  function addGift() {
    var body = {
      items: [
        {
          id: GIFT_VARIANT_ID,
          quantity: 1,
          properties: {},
        },
      ],
    };
    body.items[0].properties[GIFT_PROPERTY_KEY] = GIFT_PROPERTY_VALUE;

    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
  }

  /** @param {Object} giftLine */
  function removeGift(giftLine) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: giftLine.key, quantity: 0 }),
    });
  }

  /** @param {Object} cart */
  function notifyCartUpdated(cart) {
    document.dispatchEvent(
      new CartUpdateEvent(cart, SOURCE_ID, {
        itemCount: cart.item_count,
        source: SOURCE_ID,
      })
    );
  }

  function syncGift() {
    if (processing) {
      pendingRerun = true;
      return;
    }
    processing = true;

    fetchCart()
      .then(function (cart) {
        var giftLine = findGiftLine(cart);
        var eligible = eligibleSubtotalCents(cart) >= THRESHOLD_CENTS;

        if (eligible && !giftLine) {
          return addGift()
            .then(fetchCart)
            .then(notifyCartUpdated);
        }
        if (!eligible && giftLine) {
          return removeGift(giftLine)
            .then(fetchCart)
            .then(notifyCartUpdated);
        }
        return null;
      })
      .catch(function (error) {
        // eslint-disable-next-line no-console
        console.error('[free-gift-auto-add]', error);
      })
      .finally(function () {
        processing = false;
        if (pendingRerun) {
          pendingRerun = false;
          syncGift();
        }
      });
  }

  document.addEventListener('cart:update', function (event) {
    if (event && event.detail && event.detail.sourceId === SOURCE_ID) return;
    syncGift();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncGift);
  } else {
    syncGift();
  }
})();
